import { Component, OnInit, Inject, Output, EventEmitter, OnDestroy, ViewChild } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule, MatDrawer, MatSidenav } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { RouterOutlet } from '@angular/router';
import { MsalService, MsalModule, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus, PopupRequest, RedirectRequest, EventMessage, EventType, AccountInfo } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LoadingSpinnerModule } from './shared/loading-spinner/loading-spinner.module';
import { UtilitiesService } from "./shared/services/utilities.service";
import { UserService } from "./shared/services/user.service";
import { FeedbackService } from "./shared/services/feedback.service";
import { HeaderModule } from "./shared/header/header.module";
import { FooterModule } from "./shared/footer/footer.module";
import { NavigationBarModule } from "./shared/navigation-bar/navigation-bar.module";
import { FormsComponentModule } from "./forms/forms.module";
import { EmailTemplateModule } from "./email-template/email-template.module";
import { LetterTemplateModule } from './letter-template/letter-template.module';
import { TextMessageTemplateModule } from './text-message-template/text-message-template.module';
import { QuestionGroupsModule } from './question-groups/question-groups.module';
import { isIE } from "../environments/environment";

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  standalone: true,
  imports: [CommonModule, 
            MsalModule, 
            RouterOutlet, 
            FormsModule, 
            ReactiveFormsModule, 
            RouterLink, 
            MatCardModule, 
            MatToolbarModule, 
            MatTooltipModule, 
            MatButtonModule, 
            MatMenuModule, 
            MatSidenavModule, 
            MatListModule,
            MatIconModule,
            HeaderModule, 
            FooterModule, 
            NavigationBarModule, 
            LoadingSpinnerModule,
            FormsComponentModule,
            EmailTemplateModule,
            LetterTemplateModule,
            TextMessageTemplateModule,
            QuestionGroupsModule
          ]
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'web-admin-ui-hfsv';
  @ViewChild("Drawer", { static: false }) drawer: MatDrawer;
  @ViewChild('SideNavMenu') sidenav: MatSidenav;
  @Output() sendCloseMenu: EventEmitter<boolean> = new EventEmitter();

  isSrfsOrAdminUser: boolean = false;
  isMenuOpened: boolean = false;
  isIframe = false;
  loginDisplay = false;
  activeAccount: AccountInfo | null = null;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private userService: UserService,
    private utilitiesService: UtilitiesService,
    private feedbackService: FeedbackService
  ) { }

  ngOnInit(): void {
    this.authService.handleRedirectObservable().subscribe();
    this.isIframe = window !== window.parent && !window.opener;

    this.setLoginDisplay();

    this.authService.instance.enableAccountStorageEvents();
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
      )
      .subscribe((result: EventMessage) => {
        //console.log("appcomp - msal acc chk");
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = "/";
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        console.log("appcomp - msal inprogress");
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      })
  }

  toggleUserOptionsMenu() {
    console.log("togggle user options menu");
    if (this.drawer.opened) {
      this.drawer.close();
    } else {
      this.drawer.open();
    }
  }

  toggleMainMenu($event: boolean) {
    console.log("togggle main menu - " + this.isMenuOpened);
    this.isMenuOpened = $event;
    if (this.sidenav.opened) {
      if (!this.isMenuOpened) {
        this.sidenav.close();
      }
    } else {
      if (this.isMenuOpened) {
        this.sidenav.open();
      }
    }
  }

  closeMenu() {
    console.log("close menu button");
    this.isMenuOpened = false;
    this.sidenav.close();
    this.sendCloseMenu.emit(false);
  }

  signout() {
    //this.userService.signout();
    if(isIE){
      this.logout(false);
    }
    else{
       this.logout(true);
    }
    this.utilitiesService.clearStateOnLogOut();
  }

  get userInitials(): string | null {
    return this.userService.getUserInitials();
  }

  get userName(): string | null {
    return this.userService.getUserFullName();
  }

  get userEmail(): string | null {
    return this.userService.getCurrentUser();
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      activeAccount = accounts[0];
      this.authService.instance.setActiveAccount(activeAccount);
      this.updateActiveAccInfo(activeAccount);
    }

    if (activeAccount) {
      //must be an admin
      this.userService.checkIfLoggedInUserAdminByEmailId(activeAccount!.username).subscribe(
        response => {
          if (!response) {
            this.feedbackService.error(
              "You are not authorised to use the application."
            );

            setTimeout(() => {
              this.signout();
            }, 3000);
          }
        },
        (error) => {
          console.log("ac - An error was encountered -> " + error);
        });
    }
   
  }

  updateActiveAccInfo(activeAccount: AccountInfo): void {
    //pass activeAcc to user service
    this.userService.account = activeAccount;
    //console.log("active acc");
  }

  loginRedirect() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  loginPopup() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
        });
    } else {
      this.authService.loginPopup()
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
        });
    }
  }

  logout(popup?: boolean) {
    if (popup) {
      this.authService.logoutPopup({
        mainWindowRedirectUri: "/"
      });
    } else {
      this.authService.logoutRedirect();
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}

